// ─── EditSaveField — extracted from AdminCCFSLeads.jsx (Phase 1) ───
// Single-line inline edit field with a Save button. Calls parent-supplied onSave; owns
// only local edit/draft/saving state. No API or business logic.

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, Check, X, ArrowRight } from 'lucide-react';
import { fmtDatePretty } from '../utils';

export function EditSaveField({ label, value, type = 'text', placeholder, onSave, testId, linkButton }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value); }, [value]);

  const handleSave = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const displayValue = type === 'date' && value
    ? fmtDatePretty(value)
    : value;

  return (
    <div>
      <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold mb-1">{label}</p>
      {!editing ? (
        <div className="flex items-center gap-2">
          {value ? (
            <span className="text-xs text-slate-700 font-medium bg-slate-50 px-2.5 py-1.5 rounded border border-slate-100 flex-1 truncate" data-testid={`${testId}-display`}>
              {type === 'url' ? <span className="text-blue-600 underline">{displayValue}</span> : displayValue}
            </span>
          ) : (
            <span className="text-xs text-slate-300 italic bg-slate-50 px-2.5 py-1.5 rounded border border-slate-100 flex-1">{placeholder || 'Not set'}</span>
          )}
          <button onClick={() => { setDraft(value); setEditing(true); }} className="text-slate-400 hover:text-[#1e3a5f] transition-colors p-1 rounded hover:bg-slate-100" data-testid={`${testId}-edit`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
          </button>
          {linkButton && (
            <a href={linkButton.href} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid={`${testId}-link`}>
                <ArrowRight className="w-3 h-3" /> {linkButton.label}
              </Button>
            </a>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type={type}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            placeholder={placeholder}
            className="flex-1 text-xs px-2.5 py-1.5 rounded border border-blue-300 bg-white ring-1 ring-blue-100 outline-none"
            autoFocus
            data-testid={`${testId}-input`}
          />
          <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`${testId}-save`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </Button>
          <button onClick={() => { setDraft(value); setEditing(false); }} className="text-slate-400 hover:text-red-500 p-1" data-testid={`${testId}-cancel`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
